import { Box } from '@atlaskit/primitives/box';

import { xcss } from '@atlaskit/primitives/xcss/xcss';

import GettingStartedCard from '@atlassian/getting-started-widget/getting-started-widget';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { useMessageContext } from '@atlassian/post-office-context/message-context';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { type FunctionComponent, useCallback, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import { type CarouselBaseCardProps } from './CarouselBaseCardTypes';

/**
 * If updating widths, please update the following places
 * placement - post-office/integrated-teams/townsquare/placement-v2/src/townsquare-onboarding-widget/constants.ts
 * message template VR tests
 *  */
const visibleStyles = xcss({
	maxWidth: '360px',
	minWidth: '216px',
	width: '100%',
});

const invisibleStyles = xcss({
	opacity: 0,
	pointerEvents: 'none',
	visibility: 'hidden',
	width: '0px',
	height: '0px',
});

const CarouselBaseCard: FunctionComponent<CarouselBaseCardProps> = ({
	messages,
	images,
	variant,
	isVisible,
	isCarouselOpen,
	dismiss,
	onClick,
	displayUrl,
	enableCompletedOnClick = true,
}) => {
	const { onClosed } = usePostOfficeUserInteractions();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { messageTemplateId } = useMessageContext();

	const handleDismiss = useCallback(() => {
		void onClosed({ interactionIdentifier: 'dismiss' });
		dismiss();
	}, [onClosed, dismiss]);

	const handleClick = () => {
		onClick && onClick();

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'carouselCard',
			actionSubjectId: 'townsquareOnboardingCarouselCard',
			attributes: { messageTemplateId },
		});
	};

	useEffect(() => {
		if (!isCarouselOpen) {
			handleDismiss();
		}
	}, [isCarouselOpen, handleDismiss]);

	return (
		<Box xcss={isVisible ? visibleStyles : invisibleStyles} aria-hidden={!isVisible}>
			<GettingStartedCard
				image={images}
				heading={<FormattedMessage {...messages.heading} />}
				description={<FormattedMessage {...messages.description} />}
				ctaText={<FormattedMessage {...messages.cta} />}
				ctaCompletedText={<FormattedMessage {...messages.completedStatus} />}
				dismissTooltip={<FormattedMessage {...messages.dismissTooltip} />}
				onClick={handleClick}
				onDismiss={handleDismiss}
				enableCompletedOnClick={enableCompletedOnClick}
				headingSize="xsmall"
				hasBorder
				cardCompleted={variant === 'completed'}
				hrefLink={displayUrl}
				showAsExternalLink={false}
			/>
		</Box>
	);
};

export { CarouselBaseCard };
